import axios from 'axios'
import requestV2 from '@/service/request-v2'
import { normalizePageResult } from '@/service/adapters/page-result'
import { reverseTimestamp } from '@/utils/date-format'
import type { PageQuery, PageResult } from '@/types/crud'

export type ProductStatus = '0' | '1'
export type ProductImageType = 'cover' | 'payQrCode'

export interface ProductFilters {
  liveRoom: string
  name: string
  isShow: ProductStatus | ''
}

export interface ProductRecord {
  id: number | string
  liveRoom: string
  name: string
  cover: string | null
  price: number | string
  payQrCode: string | null
  isShow: ProductStatus
  hotSell: ProductStatus
  updateTime: string
  buyLink: string
  monthPrice: number | string
  twoMonthPrice: number | string
  threeMonthPrice: number | string
  fourMonthPrice: number | string
  fiveMonthPrice: number | string
  sixMonthPrice: number | string
  yearPrice: number | string
  deposit: number | string
}

type ProductWireRecord = Omit<ProductRecord, 'isShow' | 'hotSell'> & {
  isShow: unknown
  hotSell: unknown
}

export interface ProductPayload {
  name: string
  monthPrice: number | string
  twoMonthPrice: number | string
  threeMonthPrice: number | string
  fourMonthPrice: number | string
  fiveMonthPrice: number | string
  sixMonthPrice: number | string
  yearPrice: number | string
  deposit: number | string
  buyLink: string
  liveRoom: string
}

export interface LiveRoomOption {
  unionId: string
  eventName: string
}

interface OssPolicy extends Record<string, string | number> {
  host: string
  dir: string
  accessId: string
}

function normalizeProductStatus(
  value: unknown,
  field: 'isShow' | 'hotSell'
): ProductStatus {
  if (value === '1' || value === 1) return '1'
  if (value === '0' || value === 0 || value === null) return '0'
  throw new Error(`商品列表 ${field} 状态无效`)
}

export async function fetchProductPage(
  query: PageQuery<ProductFilters>,
  signal: AbortSignal
): Promise<PageResult<ProductRecord>> {
  const payload = await requestV2.get<unknown>({
    url: '/main/product/queryByCondition',
    params: {
      ...query.filters,
      pageNo: query.pageNo,
      pageSize: query.pageSize
    },
    signal
  })
  const page = normalizePageResult<ProductWireRecord>(payload)
  return {
    ...page,
    records: page.records.map((record) => ({
      ...record,
      isShow: normalizeProductStatus(record.isShow, 'isShow'),
      hotSell: normalizeProductStatus(record.hotSell, 'hotSell')
    }))
  }
}

export async function fetchLiveRoomOptions() {
  const payload = await requestV2.get<unknown>({
    url: '/main/customerEvents/queryByCondition',
    params: { pageNo: 1, pageSize: 1000 }
  })
  return normalizePageResult<LiveRoomOption>(payload).records
}

export function createProduct(payload: ProductPayload) {
  return requestV2.post<void>({ url: '/main/product/add', data: payload })
}

export function updateProduct(
  id: ProductRecord['id'],
  payload: ProductPayload
) {
  return requestV2.put<void>({ url: `/main/product/${id}`, data: payload })
}

export function deleteProduct(id: ProductRecord['id']) {
  return requestV2.delete<void>({ url: `/main/product/${id}` })
}

export function updateProductVisible(
  id: ProductRecord['id'],
  isShow: ProductStatus
) {
  return requestV2.post<void>({
    url: '/main/product/uploadVisible',
    params: { id, isShow }
  })
}

export function updateProductHotSell(
  id: ProductRecord['id'],
  liveRoom: ProductRecord['liveRoom'],
  hotSell: ProductStatus
) {
  return requestV2.post<void>({
    url: '/main/product/hotSell',
    params: { id, hotSell, liveRoom }
  })
}

export function updateProductImage(
  id: ProductRecord['id'],
  type: ProductImageType,
  imageUrl: string
) {
  const isCover = type === 'cover'
  return requestV2.post<void>({
    url: isCover ? '/main/product/uploadCover' : '/main/product/uploadQrCode',
    params: isCover ? { id, cover: imageUrl } : { id, qrCode: imageUrl }
  })
}

export function deleteProductImage(
  id: ProductRecord['id'],
  type: ProductImageType
) {
  return requestV2.post<void>({
    url:
      type === 'cover'
        ? '/main/product/deleteCover'
        : '/main/product/deleteQrCode',
    params: { id }
  })
}

export async function uploadProductAsset(file: File) {
  const policy = await requestV2.get<OssPolicy>({ url: '/oss/policy' })
  if (
    !policy ||
    typeof policy !== 'object' ||
    typeof policy.host !== 'string' ||
    typeof policy.dir !== 'string' ||
    typeof policy.accessId !== 'string'
  ) {
    throw new Error('上传凭证响应不完整')
  }

  const extension = file.name.slice(file.name.lastIndexOf('.'))
  const key = `${policy.dir}${reverseTimestamp()}${extension}`
  const formData = new FormData()
  Object.entries(policy).forEach(([name, value]) => {
    formData.append(name, String(value))
  })
  formData.set('OSSAccessKeyId', policy.accessId)
  formData.set('key', key)
  formData.append('file', file)

  await axios.post(policy.host, formData)
  return `${policy.host}/${key}`
}
